import { HeroIngredient, MorningNightProduct } from "@cureskin/api-client/src/server-api";

interface RegimenWithMorningNightProducts {
  morning?: MorningNightProduct[];
  night?: MorningNightProduct[];
}

export interface ExtractedRegimenProduct extends MorningNightProduct {
  use: Array<'morning' | 'night'>;
}

/**
 * Extract unique regimen products
 *
 * This function will return the list of products in regimen.
 * All the products will have an extra property `use`, which contains the
 * 'monring', 'night' or both based on the usage.
 *
 * The order of products array -
 * 1. all the morning products.
 * 2. all the night products excluding morning products.
 *
 * @param regimen
 * @returns array of products
*/
export function extractRegimenProducts(regimen: RegimenWithMorningNightProducts): ExtractedRegimenProduct[] {
  const productMap = new Map<string, ExtractedRegimenProduct>();
  const morningProducts = regimen?.morning;
  const nightProducts = regimen?.night;

  morningProducts?.forEach((p: MorningNightProduct): void => {
    if (!productMap.has(p.product.objectId)) productMap.set(p.product.objectId, { ...p, use: ['morning'] });
  });

  nightProducts?.forEach((p: MorningNightProduct): void => {
    if (!productMap.has(p.product.objectId)) productMap.set(p.product.objectId, { ...p, use: ['night'] });
    else productMap.get(p.product.objectId)?.use.push('night');
  });
  const uniqueProducts = Array.from(productMap.values());

  return uniqueProducts;
}

export function setRegimenPricing(param: any): any {
  if (!param) return null;
  const regimen: any = { ...param };
  regimen.totalMRP = regimen?.fixedPriceMRP;
  regimen.totalSP = regimen?.fixedPrice;
  regimen.discount = Math.floor(((regimen.totalMRP - regimen.totalSP) * 100) / regimen.totalMRP);

  return regimen;
}

export function extractHeroIngredients(products: MorningNightProduct[]): HeroIngredient[] {
  const uniqueHeroIngredients = new Map<string, HeroIngredient>();

  products.forEach((product: MorningNightProduct): void => {
    if (product.product.heroIngredients) {
      product.product.heroIngredients.forEach((ingredient: HeroIngredient): void => {
        const key = ingredient.objectId;
        if (!uniqueHeroIngredients.has(key)) {
          uniqueHeroIngredients.set(key, ingredient);
        }
      });
    }
  });
  const heroIngredients = Array.from(uniqueHeroIngredients.values());
  const priorityOf = (ingredient: HeroIngredient): number => ingredient.priority ?? Number.MAX_SAFE_INTEGER;
  return heroIngredients.sort((a: HeroIngredient, b: HeroIngredient): number => priorityOf(a) - priorityOf(b));
}

export function getRegimenProductsCount(regimen: RegimenWithMorningNightProducts): number {
  const products = extractRegimenProducts(regimen);
  return products?.length || 4;
}

const WASH_CARE_PURPOSE_REGEX = /\b(shampoo|conditioner|oil|enhancer)\b/i;

interface RegimenProductItem {
  product?: { purposeInEn?: string };
}

export function moveWashCareProductsToEnd<T extends RegimenProductItem>(products: T[]): T[] {
  const isWashCare = (product: T): boolean => WASH_CARE_PURPOSE_REGEX.test(product.product?.purposeInEn ?? '');
  return [
    ...products.filter((product: T): boolean => !isWashCare(product)),
    ...products.filter(isWashCare),
  ];
}
